import { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface PdfMeta {
  filename: string;
  bytes: number;
  pages: number;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * "Download PDF press release" action. The href is static (works without JS and
 * during prerender); after hydration it fetches lightweight metadata to show
 * the page count and file size, so the action feels polished.
 */
export function PressDownloadButton({ slug }: { slug: string }) {
  const [meta, setMeta] = useState<PdfMeta | null>(null);
  const pdfUrl = `${API_URL}/api/press/${slug}/pdf`;

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/press/${slug}/pdf/meta`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: PdfMeta | null) => {
        if (!cancelled && data) setMeta(data);
      })
      .catch(() => {
        /* button still works without the size hint */
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const detail = meta
    ? `PDF · ${meta.pages} page${meta.pages === 1 ? '' : 's'} · ${formatSize(meta.bytes)}`
    : 'PDF · A4 · for editors';

  return (
    <div className="flex flex-col items-start gap-2">
      <a
        href={pdfUrl}
        className="inline-flex items-center gap-2.5 rounded-xl bg-brand-600 px-5 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
          />
        </svg>
        Download PDF press release
      </a>
      <span className="text-xs font-medium text-surface-500">{detail}</span>
    </div>
  );
}
